import { api } from './api.js';

// 用户角色枚举
export const USER_ROLES = {
  ADMIN: 1,
  TEACHER: 2,
  JUDGE: 3,
  STUDENT: 4
};

// 声部枚举
export const VOICE_TYPES = {
  SOPRANO: 1,      // 女高音
  MEZZO_SOPRANO: 2, // 女中音
  COUNTERTENOR: 3,  // 假声男高音
  TENOR: 4,         // 男高音
  BARITONE: 5,      // 男中音
  BASS: 6           // 男低音
};

// 角色名称映射
export const ROLE_NAMES = {
  [USER_ROLES.ADMIN]: '管理员',
  [USER_ROLES.TEACHER]: '老师',
  [USER_ROLES.JUDGE]: '评委',
  [USER_ROLES.STUDENT]: '学生'
};

// 声部名称映射 - 根据 init.sql 定义
export const VOICE_TYPE_NAMES = {
  [VOICE_TYPES.SOPRANO]: '女高',
  [VOICE_TYPES.MEZZO_SOPRANO]: '女中',
  [VOICE_TYPES.COUNTERTENOR]: '假声男高',
  [VOICE_TYPES.TENOR]: '男高',
  [VOICE_TYPES.BARITONE]: '男中',
  [VOICE_TYPES.BASS]: '男低'
};

class AuthService {
  constructor() {
    this.baseURL = '/auth';
  }

  // 用户注册
  async register(userData) {
    const response = await api.post(`${this.baseURL}/register`, userData);
    
    if (response.success && response.data.tokens) {
      this.setTokens(response.data.tokens);
      this.setUser(response.data.user);
    }
    
    return response;
  }

  // 用户登录
  async login(credentials) {
    const response = await api.post(`${this.baseURL}/login`, credentials);
    
    if (response.success && response.data.tokens) {
      this.setTokens(response.data.tokens);
      this.setUser(response.data.user);
    }
    
    return response;
  }

  // 获取用户信息（不缓存，每次都从服务器获取最新数据）
  async getProfile() {
    // 添加时间戳参数防止浏览器缓存
    const timestamp = Date.now();
    const response = await api.get(`${this.baseURL}/profile?t=${timestamp}`);

    // 不在这里缓存用户数据，让store来决定是否缓存
    // 这样确保每次都获取最新数据

    return response;
  }

  // 更新用户信息
  async updateProfile(userData) {
    const response = await api.put(`${this.baseURL}/profile`, userData);

    // 不在这里设置用户数据，让store来处理
    return response;
  }

  // 修改密码
  async changePassword(passwordData) {
    return await api.put(`${this.baseURL}/password`, passwordData);
  }

  // 获取系统枚举值
  async getEnums() {
    return await api.get(`${this.baseURL}/enums`);
  }

  // 用户登出
  logout() {
    this.clearTokens();
    this.clearUser();
    window.location.href = '/login';
  }

  // Token管理
  setTokens(tokens) {
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
  }

  getAccessToken() {
    return localStorage.getItem('accessToken');
  }

  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  clearTokens() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  // 用户信息管理
  setUser(user) {
    localStorage.setItem('user', JSON.stringify(user));
  }

  getUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  clearUser() {
    localStorage.removeItem('user');
  }

  // 检查是否已登录
  isAuthenticated() {
    return !!this.getAccessToken();
  }

  // 检查用户角色
  hasRole(role) {
    const user = this.getUser();
    return user && user.role === role;
  }

  // 检查是否为管理员
  isAdmin() {
    return this.hasRole(USER_ROLES.ADMIN);
  }

  // 检查是否为老师
  isTeacher() {
    return this.hasRole(USER_ROLES.TEACHER);
  }

  // 检查是否为学生
  isStudent() {
    return this.hasRole(USER_ROLES.STUDENT);
  }

  // 获取用户角色名称
  getUserRoleName() {
    const user = this.getUser();
    return user ? ROLE_NAMES[user.role] || '未知' : '未登录';
  }

  // 获取用户声部名称
  getUserVoiceTypeName() {
    const user = this.getUser();
    return user ? VOICE_TYPE_NAMES[user.vType] || '未设置' : '未登录';
  }

  // 验证表单数据
  validateRegisterData(data) {
    const errors = {};

    if (!data.name || data.name.trim().length < 2) {
      errors.name = '姓名至少需要2个字符';
    }

    if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.email = '请输入有效的邮箱地址';
    }

    if (!data.phoneNumber || !/^1[3-9]\d{9}$/.test(data.phoneNumber)) {
      errors.phoneNumber = '请输入有效的手机号码';
    }

    if (!data.password || data.password.length < 6) {
      errors.password = '密码至少需要6个字符';
    }

    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(data.password)) {
      errors.password = '密码必须包含大小写字母和数字';
    }

    if (data.password !== data.confirmPassword) {
      errors.confirmPassword = '确认密码与密码不匹配';
    }

    if (!data.role || ![USER_ROLES.TEACHER, USER_ROLES.STUDENT].includes(data.role)) {
      errors.role = '请选择有效的身份';
    }

    if (!data.vType || !Object.values(VOICE_TYPES).includes(data.vType)) {
      errors.vType = '请选择有效的声部';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  validateLoginData(data) {
    const errors = {};

    if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.email = '请输入有效的邮箱地址';
    }

    if (!data.password) {
      errors.password = '请输入密码';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
}

export default new AuthService();
